import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import DeliveryDiningRoundedIcon from '@mui/icons-material/DeliveryDiningRounded';
import StoreMallDirectoryRoundedIcon from '@mui/icons-material/StoreMallDirectoryRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';
import Groups2RoundedIcon from '@mui/icons-material/Groups2Rounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { collection, getDocs, query, Timestamp, where } from 'firebase/firestore';
import DateRangePicker from '../components/DateRangePicker';
import { DateRange } from '../models/CustomerStats';
import DateUtils from '../utils/dateUtils';
import orderService from '../services/orderService';
import { DeliveryMethod, Order, OrderFilters, PaymentMethod } from '../models/Order';
import productService from '../services/productService';
import { Product } from '../models/Product';
import { storeService } from '../services/storeService';
import { Store } from '../models/Store';
import customerService from '../services/customerService';
import salesScopeDailyService, {
  SalesScopeAggregate,
  SalesScopeDailySummary,
} from '../services/salesScopeDailyService';
import { db } from '../services/firebase';
import { useAdminScope } from '../contexts/AdminScopeContext';
import { describeActiveScope, filterOrdersByStoreScope, resolveScopedStoreIds } from '../utils/adminScopeUtils';

type RangePreset = 'yesterday' | 'today' | 'salesDay' | 'last7Days' | 'last30Days' | 'thisMonth' | 'thisYear' | 'custom';
type FilterValue = 'all' | string;

type ProductAggregate = {
  id: string;
  name: string;
  category: string;
  orderCount: number;
  quantity: number;
  isFeatured: boolean;
};

type CategoryAggregate = {
  name: string;
  orderCount: number;
  quantity: number;
};

type StoreAggregate = {
  name: string;
  revenue: number;
  orders: number;
};

type TimelinePoint = {
  label: string;
  revenue: number;
  orders: number;
};

type DerivedStats = {
  totalOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  deliveryOrders: number;
  pickupOrders: number;
  deliveryRevenue: number;
  totalDeliveryFees: number;
  totalDriverCommission: number;
  netDeliveryRevenue: number;
  pickupRevenue: number;
  cardOrders: number;
  cashOrders: number;
  cardRevenue: number;
  cashRevenue: number;
  deliveredOrders: number;
  cancelledOrders: number;
  activeOrders: number;
  promoUses: number;
  promoAmountTotal: number;
  uniquePromoCodes: number;
  topProducts: ProductAggregate[];
  topCategories: CategoryAggregate[];
  featuredProducts: ProductAggregate[];
  topStore?: StoreAggregate;
  timeline: TimelinePoint[];
};

type AggregateDrivenStats = Pick<
  DerivedStats,
  | 'totalOrders'
  | 'totalRevenue'
  | 'averageOrderValue'
  | 'deliveryOrders'
  | 'pickupOrders'
  | 'deliveryRevenue'
  | 'totalDeliveryFees'
  | 'totalDriverCommission'
  | 'netDeliveryRevenue'
  | 'pickupRevenue'
  | 'cardOrders'
  | 'cashOrders'
  | 'cardRevenue'
  | 'cashRevenue'
  | 'deliveredOrders'
  | 'cancelledOrders'
  | 'activeOrders'
  | 'promoUses'
  | 'promoAmountTotal'
  | 'uniquePromoCodes'
  | 'timeline'
>;

type OtpAuthStats = {
  whatsappRegistered: number;
  smsRegistered: number;
  whatsappTechnicalFailed: number;
  smsTechnicalFailed: number;
  whatsappNotCompleted: number;
  smsNotCompleted: number;
};

const cardStyles = {
  height: '100%',
  borderRadius: 4,
  border: '1px solid',
  borderColor: 'rgba(226,232,240,0.9)',
  boxShadow: '0 18px 42px rgba(15, 23, 42, 0.08)',
  background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.96) 100%)',
};

const quickPresetOptions: Array<{ value: RangePreset; label: string }> = [
  { value: 'yesterday', label: 'Hier' },
  { value: 'today', label: "Aujourd'hui" },
  { value: 'salesDay', label: 'Journée de vente · 06:00–06:00' },
  { value: 'last7Days', label: '7 jours' },
  { value: 'last30Days', label: '30 jours' },
  { value: 'thisMonth', label: 'Ce mois' },
  { value: 'thisYear', label: 'Cette année' },
  { value: 'custom', label: 'Période personnalisée' },
];

const paymentOptions: Array<{ value: FilterValue; label: string }> = [
  { value: 'all', label: 'Tous les paiements' },
  { value: 'cash', label: 'Espèce' },
  { value: 'card', label: 'Carte' },
];

const deliveryOptions: Array<{ value: FilterValue; label: string }> = [
  { value: 'all', label: 'Tous les canaux' },
  { value: 'delivery', label: 'Livraison' },
  { value: 'pickup', label: 'Pick-up magasin' },
];

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('fr-MA', {
    maximumFractionDigits: value >= 1000 ? 0 : 1,
    minimumFractionDigits: 0,
  }).format(value);

const formatInteger = (value: number): string =>
  new Intl.NumberFormat('fr-FR', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })
    .format(Math.round(value))
    .replace(/\u202f/g, ' ');

const formatCurrencyFull = (value: number): string =>
  `${new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(value)
    .replace(/\u202f/g, ' ')} DHS`;

const formatPercent = (value: number): string => `${Math.round(value)}%`;

const formatCompact = (value: number): string => {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(2).replace(/\.00$/, '')}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return `${Math.round(value)}`;
};

const DEFAULT_DRIVER_COMMISSION_PERCENTAGE = 40;

const roundCurrency = (value: number): number => Math.round(value * 100) / 100;

const getDriverCommissionForOrder = (order: Order): number => {
  if (typeof order.driverCommissionAmount === 'number' && Number.isFinite(order.driverCommissionAmount)) {
    return roundCurrency(order.driverCommissionAmount);
  }

  const deliveryBase = Number(order.driverPayout || 0);
  if (!Number.isFinite(deliveryBase) || deliveryBase <= 0) {
    return 0;
  }

  const percentage =
    typeof order.driverCommissionPercentageSnapshot === 'number' &&
    Number.isFinite(order.driverCommissionPercentageSnapshot)
      ? Math.min(Math.max(order.driverCommissionPercentageSnapshot, 0), 100)
      : DEFAULT_DRIVER_COMMISSION_PERCENTAGE;

  return roundCurrency(deliveryBase * (percentage / 100));
};

const getPresetRange = (preset: RangePreset): DateRange | undefined => {
  switch (preset) {
    case 'yesterday':
      return DateUtils.getYesterdayRange();
    case 'today':
      return DateUtils.getTodayRange();
    case 'salesDay':
      return DateUtils.getSalesDayRange();
    case 'last7Days':
      return DateUtils.getLast7DaysRange();
    case 'last30Days':
      return DateUtils.getLast30DaysRange();
    case 'thisMonth':
      return DateUtils.getThisMonthRange();
    case 'thisYear':
      return DateUtils.getThisYearRange();
    default:
      return undefined;
  }
};

const emptyOtpAuthStats = (): OtpAuthStats => ({
  whatsappRegistered: 0,
  smsRegistered: 0,
  whatsappTechnicalFailed: 0,
  smsTechnicalFailed: 0,
  whatsappNotCompleted: 0,
  smsNotCompleted: 0,
});

type SalesStatisticsCacheEntry = {
  key: string;
  orders: Order[];
  products: Product[];
  stores: Store[];
  customerTotal: number;
  customerPeriodCount: number;
  existingUsersTotal: number;
  salesSummary: SalesScopeDailySummary | null;
  otpAuthStats: OtpAuthStats;
};

let salesStatisticsCache: SalesStatisticsCacheEntry | null = null;

const buildDateRangeQuery = (collectionName: string, dateRange: DateRange | undefined) => {
  if (!dateRange) {
    return query(collection(db, collectionName));
  }

  return query(
    collection(db, collectionName),
    where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
    where('createdAt', '<=', Timestamp.fromDate(dateRange.end))
  );
};

const loadOtpAuthStats = async (dateRange: DateRange | undefined): Promise<OtpAuthStats> => {
  const [auditSnapshot, challengesSnapshot] = await Promise.all([
    getDocs(buildDateRangeQuery('auth_audit_logs', dateRange)),
    getDocs(buildDateRangeQuery('otp_challenges', dateRange)),
  ]);

  const stats = emptyOtpAuthStats();
  const nowMs = Date.now();
  const technicalFailures = {
    whatsapp: new Set<string>(),
    sms: new Set<string>(),
  };

  auditSnapshot.forEach((doc) => {
    const data = doc.data();
    if (data.appContext !== 'client') return;
    if (data.provider === 'test') return;
    const challengeId = typeof data.challengeId === 'string' ? data.challengeId : null;

    if (data.eventType === 'otp_verify_succeeded' && data.isNewUser === true) {
      if (data.successfulChannel === 'whatsapp') {
        stats.whatsappRegistered += 1;
      } else {
        stats.smsRegistered += 1;
      }
      return;
    }

    if (data.eventType === 'otp_send_failed' || data.eventType === 'otp_delivery_failed') {
      const channel =
        data.channel === 'whatsapp' || data.provider === 'infobip_whatsapp'
          ? 'whatsapp'
          : 'sms';

      if (challengeId) {
        technicalFailures[channel].add(challengeId);
      }
    }
  });

  stats.whatsappTechnicalFailed = technicalFailures.whatsapp.size;
  stats.smsTechnicalFailed = technicalFailures.sms.size;

  challengesSnapshot.forEach((doc) => {
    const data = doc.data();
    if (data.appContext !== 'client') return;
    if (data.provider === 'test') return;
    if (data.status === 'verified') return;
    const channel = data.lastDeliveryChannel === 'whatsapp' ? 'whatsapp' : 'sms';
    const challengeId = typeof data.challengeId === 'string' ? data.challengeId : doc.id;
    const expiresAt =
      data.expiresAt instanceof Timestamp
        ? data.expiresAt.toMillis()
        : data.expiresAt?.toMillis?.() ?? null;

    const isNonCompleted =
      data.status === 'verify_failed' ||
      data.status === 'expired' ||
      (typeof expiresAt === 'number' && expiresAt <= nowMs);

    if (!isNonCompleted) return;
    if (technicalFailures[channel].has(challengeId)) return;

    if (channel === 'whatsapp') {
      stats.whatsappNotCompleted += 1;
    } else {
      stats.smsNotCompleted += 1;
    }
  });

  return stats;
};

const buildOrderFilters = (
  dateRange: DateRange | undefined,
  storeId: FilterValue,
  paymentMethod: FilterValue,
  deliveryMethod: FilterValue
): OrderFilters => {
  const filters: OrderFilters = {};

  if (dateRange) {
    filters.dateRange = dateRange;
  }

  if (storeId !== 'all') {
    filters.storeId = storeId;
  }

  if (paymentMethod !== 'all') {
    filters.paymentMethod = [paymentMethod as PaymentMethod];
  }

  if (deliveryMethod !== 'all') {
    filters.deliveryMethod = [deliveryMethod as DeliveryMethod];
  }

  return filters;
};

const generateTimeline = (orders: Order[], dateRange: DateRange | undefined): TimelinePoint[] => {
  if (!dateRange) return [];

  const buckets = new Map<string, TimelinePoint>();
  const cursor = new Date(dateRange.start);
  cursor.setHours(0, 0, 0, 0);
  const end = new Date(dateRange.end);
  end.setHours(0, 0, 0, 0);

  while (cursor <= end) {
    const key = cursor.toISOString().slice(0, 10);
    buckets.set(key, {
      label: cursor.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
      revenue: 0,
      orders: 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  orders.forEach((order) => {
    const createdAt = new Date(order.createdAt);
    createdAt.setHours(0, 0, 0, 0);
    const key = createdAt.toISOString().slice(0, 10);
    const bucket = buckets.get(key);
    if (!bucket) return;
    bucket.revenue += order.totalAmount || 0;
    bucket.orders += 1;
  });

  return Array.from(buckets.values());
};

const buildOrderDayKey = (date: Date): string => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized.toISOString().slice(0, 10);
};

const hasCompleteAggregateCoverage = (
  aggregateStats: SalesScopeDailySummary | null,
  orders: Order[]
): boolean => {
  if (!aggregateStats) {
    return false;
  }

  if (aggregateStats.totalOrders !== orders.length) {
    return false;
  }

  const expectedDayKeys = new Set(orders.map((order) => buildOrderDayKey(order.createdAt)));
  const coveredDayKeys = new Set(aggregateStats.coveredDayKeys);

  for (const dayKey of Array.from(expectedDayKeys.values())) {
    if (!coveredDayKeys.has(dayKey)) {
      return false;
    }
  }

  return true;
};

const toAggregateDrivenStats = (
  aggregateStats: SalesScopeDailySummary
): AggregateDrivenStats => ({
  totalOrders: aggregateStats.totalOrders,
  totalRevenue: aggregateStats.totalRevenue,
  averageOrderValue: aggregateStats.averageOrderValue,
  deliveryOrders: aggregateStats.deliveryOrders,
  pickupOrders: aggregateStats.pickupOrders,
  deliveryRevenue: aggregateStats.deliveryRevenue,
  totalDeliveryFees: aggregateStats.totalDeliveryFees,
  totalDriverCommission: aggregateStats.totalDriverCommission,
  netDeliveryRevenue: aggregateStats.netDeliveryRevenue,
  pickupRevenue: aggregateStats.pickupRevenue,
  cardOrders: aggregateStats.cardOrders,
  cashOrders: aggregateStats.cashOrders,
  cardRevenue: aggregateStats.cardRevenue,
  cashRevenue: aggregateStats.cashRevenue,
  deliveredOrders: aggregateStats.deliveredOrders,
  cancelledOrders: aggregateStats.cancelledOrders,
  activeOrders: aggregateStats.activeOrders,
  promoUses: aggregateStats.promoUses,
  promoAmountTotal: aggregateStats.promoAmountTotal,
  uniquePromoCodes: aggregateStats.uniquePromoCodes,
  timeline: aggregateStats.timeline.map((point) => ({
    label: point.label,
    revenue: point.revenue,
    orders: point.orders,
  })),
});

const buildDerivedStats = (
  orders: Order[],
  productsById: Map<string, Product>,
  dateRange: DateRange | undefined
): DerivedStats => {
  const productMap = new Map<string, ProductAggregate>();
  const categoryMap = new Map<string, CategoryAggregate>();
  const storeMap = new Map<string, StoreAggregate>();

  let totalRevenue = 0;
  let deliveryOrders = 0;
  let pickupOrders = 0;
  let deliveryRevenue = 0;
  let totalDeliveryFees = 0;
  let totalDriverCommission = 0;
  let pickupRevenue = 0;
  let cardOrders = 0;
  let cashOrders = 0;
  let cardRevenue = 0;
  let cashRevenue = 0;
  let deliveredOrders = 0;
  let cancelledOrders = 0;
  let activeOrders = 0;
  let promoUses = 0;
  let promoAmountTotal = 0;
  const promoCodes = new Set<string>();

  orders.forEach((order) => {
    totalRevenue += order.totalAmount || 0;

    if (order.deliveryMethod === 'pickup') {
      pickupOrders += 1;
      pickupRevenue += order.totalAmount || 0;
    } else {
      deliveryOrders += 1;
      deliveryRevenue += order.totalAmount || 0;
      totalDeliveryFees += order.deliveryFeeAtOrderTime || 0;
      totalDriverCommission += getDriverCommissionForOrder(order);
    }

    if (order.paymentMethod === 'card') {
      cardOrders += 1;
      cardRevenue += order.totalAmount || 0;
    } else {
      cashOrders += 1;
      cashRevenue += order.totalAmount || 0;
    }

    if (order.status === 'DELIVERED') {
      deliveredOrders += 1;
    } else if (order.status === 'CANCELLED') {
      cancelledOrders += 1;
    } else {
      activeOrders += 1;
    }

    if (order.promoCodeApplied && order.promoCodeApplied.trim().length > 0) {
      promoUses += 1;
      promoAmountTotal += order.promoAmountApplied || 0;
      promoCodes.add(order.promoCodeApplied.trim().toUpperCase());
    }

    const storeKey = order.storeId || order.storeName || 'unknown';
    const currentStore = storeMap.get(storeKey) || {
      name: order.storeName || 'Magasin inconnu',
      revenue: 0,
      orders: 0,
    };
    currentStore.revenue += order.totalAmount || 0;
    currentStore.orders += 1;
    storeMap.set(storeKey, currentStore);

    const seenProductsInOrder = new Set<string>();
    const seenCategoriesInOrder = new Set<string>();

    order.items.forEach((item) => {
      const product = productsById.get(item.productId);
      const productName = product?.name || 'Produit inconnu';
      const categoryName = product?.category || 'Sans catégorie';
      const quantity = item.quantity || 0;

      const currentProduct = productMap.get(item.productId) || {
        id: item.productId,
        name: productName,
        category: categoryName,
        orderCount: 0,
        quantity: 0,
        isFeatured: product?.isFeatured === true,
      };

      currentProduct.quantity += quantity;
      if (!seenProductsInOrder.has(item.productId)) {
        currentProduct.orderCount += 1;
        seenProductsInOrder.add(item.productId);
      }
      productMap.set(item.productId, currentProduct);

      const currentCategory = categoryMap.get(categoryName) || {
        name: categoryName,
        orderCount: 0,
        quantity: 0,
      };
      currentCategory.quantity += quantity;
      if (!seenCategoriesInOrder.has(categoryName)) {
        currentCategory.orderCount += 1;
        seenCategoriesInOrder.add(categoryName);
      }
      categoryMap.set(categoryName, currentCategory);
    });
  });

  const topProducts = Array.from(productMap.values()).sort((a, b) => {
    if (b.quantity !== a.quantity) return b.quantity - a.quantity;
    return b.orderCount - a.orderCount;
  });

  const topCategories = Array.from(categoryMap.values()).sort((a, b) => {
    if (b.orderCount !== a.orderCount) return b.orderCount - a.orderCount;
    return b.quantity - a.quantity;
  });

  const featuredProducts = topProducts.filter((product) => product.isFeatured);
  const topStore = Array.from(storeMap.values()).sort((a, b) => b.revenue - a.revenue)[0];

  return {
    totalOrders: orders.length,
    totalRevenue,
    averageOrderValue: orders.length > 0 ? totalRevenue / orders.length : 0,
    deliveryOrders,
    pickupOrders,
    deliveryRevenue,
    totalDeliveryFees: roundCurrency(totalDeliveryFees),
    totalDriverCommission: roundCurrency(totalDriverCommission),
    netDeliveryRevenue: roundCurrency(totalDeliveryFees - totalDriverCommission),
    pickupRevenue,
    cardOrders,
    cashOrders,
    cardRevenue,
    cashRevenue,
    deliveredOrders,
    cancelledOrders,
    activeOrders,
    promoUses,
    promoAmountTotal,
    uniquePromoCodes: promoCodes.size,
    topProducts,
    topCategories,
    featuredProducts,
    topStore,
    timeline: generateTimeline(orders, dateRange),
  };
};

const buildAreaPath = (values: number[], width: number, height: number) => {
  if (values.length === 0) return { line: '', area: '' };

  const max = Math.max(...values, 1);
  const stepX = values.length > 1 ? width / (values.length - 1) : width;

  const points = values.map((value, index) => {
    const x = index * stepX;
    const y = height - (value / max) * (height - 24) - 12;
    return { x, y };
  });

  const line = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

  const area = `${line} L ${width} ${height} L 0 ${height} Z`;

  return { line, area };
};

interface KpiCardProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  footerLeft: string;
  footerRight: string;
  trend?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ icon, title, value, footerLeft, footerRight, trend }) => (
  <Card sx={cardStyles}>
    <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 2.5,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'rgba(39, 168, 174, 0.12)',
            color: 'primary.dark',
          }}
        >
          {icon}
        </Box>
        {trend ? (
          <Chip
            label={trend}
            size="small"
            sx={{
              bgcolor: 'rgba(22, 163, 74, 0.12)',
              color: '#15803d',
              fontWeight: 800,
            }}
          />
        ) : null}
      </Stack>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          letterSpacing: '-0.04em',
          mb: 0.5,
          lineHeight: 1.1,
          fontSize:
            value.length > 20
              ? { xs: '1.18rem', sm: '1.35rem', md: '1.55rem' }
              : value.length > 14
                ? { xs: '1.35rem', sm: '1.65rem', md: '1.9rem' }
                : undefined,
        }}
      >
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {title}
      </Typography>

      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography variant="caption" color="text.secondary">
          {footerLeft}
        </Typography>
        <Typography variant="caption" color="text.secondary" textAlign="right">
          {footerRight}
        </Typography>
      </Stack>
    </CardContent>
  </Card>
);

interface DeliveryMarginCardProps {
  totalDeliveryFees: number;
  totalDriverCommission: number;
  netDeliveryRevenue: number;
  deliveryOrders: number;
}

const DeliveryMarginCard: React.FC<DeliveryMarginCardProps> = ({
  totalDeliveryFees,
  totalDriverCommission,
  netDeliveryRevenue,
  deliveryOrders,
}) => (
  <Card sx={cardStyles}>
    <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.35} spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(39, 168, 174, 0.12)',
              color: 'primary.dark',
            }}
          >
            <DeliveryDiningRoundedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.1, fontSize: '0.98rem' }}>
              CA livraison
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
              Frais, commission drivers et reste net.
            </Typography>
          </Box>
        </Stack>
        <Chip
          size="small"
          label={`${formatInteger(deliveryOrders)} livr.`}
          sx={{ fontWeight: 800, height: 24, '& .MuiChip-label': { px: 1, fontSize: '0.72rem' } }}
        />
      </Stack>

      <Stack spacing={0.35}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ py: 0.15, borderBottom: '1px solid rgba(226,232,240,0.75)' }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.73rem' }}>
            Total frais livraison
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.78rem' }}>
            {formatCurrencyFull(totalDeliveryFees)}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ py: 0.15, borderBottom: '1px solid rgba(226,232,240,0.75)' }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.73rem' }}>
            Total comission drivers
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.78rem' }}>
            {formatCurrencyFull(totalDriverCommission)}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ pt: 0.15 }}
        >
          <Typography variant="caption" sx={{ fontWeight: 800, color: '#166534', fontSize: '0.73rem' }}>
            Reste CA de livraison
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 900, color: '#166534', fontSize: '0.8rem' }}>
            {formatCurrencyFull(netDeliveryRevenue)}
          </Typography>
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

interface OtpAuthCardProps {
  stats: OtpAuthStats;
}

const OtpAuthCard: React.FC<OtpAuthCardProps> = ({ stats }) => {
  const totalRegistered = stats.whatsappRegistered + stats.smsRegistered;
  const totalTechnicalFailed = stats.whatsappTechnicalFailed + stats.smsTechnicalFailed;
  const totalNotCompleted = stats.whatsappNotCompleted + stats.smsNotCompleted;

  return (
    <Card sx={cardStyles}>
      <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(39, 168, 174, 0.12)',
                color: 'primary.dark',
              }}
            >
              <ForumRoundedIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                Authentification OTP
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Inscriptions, échecs techniques connus et sessions non abouties par canal.
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={`${formatInteger(totalRegistered)} inscrits • ${formatInteger(totalNotCompleted)} non aboutis • ${formatInteger(totalTechnicalFailed)} échecs tech.`}
            sx={{ fontWeight: 800 }}
          />
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))' },
            gap: 1.5,
          }}
        >
          <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.8)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <WhatsAppIcon sx={{ color: '#16a34a' }} />
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  WhatsApp
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Canal direct
              </Typography>
            </Stack>

            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.05 }}>
              {formatInteger(stats.whatsappRegistered)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              nouveaux comptes créés
            </Typography>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={0.8} alignItems="center">
                <CheckCircleOutlineRoundedIcon sx={{ fontSize: 18, color: '#16a34a' }} />
                <Typography variant="caption" color="text.secondary">
                  Réussis
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {formatInteger(stats.whatsappRegistered)}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.6}>
              <Stack direction="row" spacing={0.8} alignItems="center">
                <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                <Typography variant="caption" color="text.secondary">
                  Échecs techniques
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#b91c1c' }}>
                {formatInteger(stats.whatsappTechnicalFailed)}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.6}>
              <Stack direction="row" spacing={0.8} alignItems="center">
                <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: '#ea580c' }} />
                <Typography variant="caption" color="text.secondary">
                  Non aboutis
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#c2410c' }}>
                {formatInteger(stats.whatsappNotCompleted)}
              </Typography>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.8)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SmsRoundedIcon sx={{ color: 'primary.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  SMS
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Canal classique
              </Typography>
            </Stack>

            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.05 }}>
              {formatInteger(stats.smsRegistered)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              nouveaux comptes créés
            </Typography>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={0.8} alignItems="center">
                <CheckCircleOutlineRoundedIcon sx={{ fontSize: 18, color: '#16a34a' }} />
                <Typography variant="caption" color="text.secondary">
                  Réussis
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {formatInteger(stats.smsRegistered)}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.6}>
              <Stack direction="row" spacing={0.8} alignItems="center">
                <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                <Typography variant="caption" color="text.secondary">
                  Échecs techniques
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#b91c1c' }}>
                {formatInteger(stats.smsTechnicalFailed)}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.6}>
              <Stack direction="row" spacing={0.8} alignItems="center">
                <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: '#ea580c' }} />
                <Typography variant="caption" color="text.secondary">
                  Non aboutis
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#c2410c' }}>
                {formatInteger(stats.smsNotCompleted)}
              </Typography>
            </Stack>
          </Paper>
        </Box>
      </CardContent>
    </Card>
  );
};

interface RevenueChartProps {
  timeline: TimelinePoint[];
}

const RevenueChart: React.FC<RevenueChartProps> = ({ timeline }) => {
  const values = timeline.map((point) => point.revenue);
  const { line, area } = buildAreaPath(values, 760, 260);
  const highestDay = timeline.reduce<TimelinePoint | null>((best, current) => {
    if (!best || current.revenue > best.revenue) return current;
    return best;
  }, null);

  return (
    <Card sx={cardStyles}>
      <CardContent sx={{ p: 2.75 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Évolution du chiffre d’affaires
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Revenus journaliers et volume de commandes sur la période choisie.
            </Typography>
          </Box>
          {highestDay ? (
            <Chip
              label={`Pic : ${highestDay.label} • ${formatCurrency(highestDay.revenue)} DH`}
              sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
            />
          ) : null}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, bgcolor: 'grey.50' }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {timeline.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Jours analysés
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, bgcolor: 'grey.50' }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {formatCurrency(values.reduce((sum, value) => sum + value, 0) / Math.max(values.length || 1, 1))}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              CA moyen / jour
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, bgcolor: 'grey.50' }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {formatCurrency(Math.max(...values, 0))}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Meilleure journée
            </Typography>
          </Paper>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 720 }}>
            <svg viewBox="0 0 820 320" width="100%" height="320" role="img" aria-label="Courbe du chiffre d'affaires">
              <defs>
                <linearGradient id="salesAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#27A8AE" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="#27A8AE" stopOpacity="0.02" />
                </linearGradient>
              </defs>
              <g transform="translate(34 20)">
                <line x1="0" y1="0" x2="0" y2="260" stroke="#dbe4ee" />
                <line x1="0" y1="260" x2="760" y2="260" stroke="#dbe4ee" />
                <line x1="0" y1="195" x2="760" y2="195" stroke="#edf2f7" />
                <line x1="0" y1="130" x2="760" y2="130" stroke="#edf2f7" />
                <line x1="0" y1="65" x2="760" y2="65" stroke="#edf2f7" />

                {area ? <path d={area} fill="url(#salesAreaGradient)" /> : null}
                {line ? <path d={line} fill="none" stroke="#27A8AE" strokeWidth="5" strokeLinecap="round" /> : null}

                {timeline.map((point, index) => {
                  if (timeline.length === 0) return null;
                  const stepX = timeline.length > 1 ? 760 / (timeline.length - 1) : 760;
                  const max = Math.max(...values, 1);
                  const x = index * stepX;
                  const y = 260 - (point.revenue / max) * (260 - 24) - 12;
                  return (
                    <circle key={`${point.label}-${index}`} cx={x} cy={y} r="4.5" fill="#27A8AE" />
                  );
                })}

                {timeline.map((point, index) => {
                  const labelsToShow = timeline.length > 10 ? index % Math.ceil(timeline.length / 6) === 0 || index === timeline.length - 1 : true;
                  if (!labelsToShow) return null;
                  const stepX = timeline.length > 1 ? 760 / (timeline.length - 1) : 760;
                  return (
                    <text key={`label-${point.label}-${index}`} x={index * stepX} y="292" fill="#64748b" fontSize="12" textAnchor="middle">
                      {point.label}
                    </text>
                  );
                })}
              </g>
            </svg>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

interface PaymentBreakdownProps {
  cashRevenue: number;
  cardRevenue: number;
  cashOrders: number;
  cardOrders: number;
  totalRevenue: number;
}

const PaymentBreakdownCard: React.FC<PaymentBreakdownProps> = ({
  cashRevenue,
  cardRevenue,
  cashOrders,
  cardOrders,
  totalRevenue,
}) => {
  const normalizedTotal = Math.max(cashRevenue + cardRevenue, 1);
  const cashPercent = (cashRevenue / normalizedTotal) * 100;

  return (
    <Card sx={cardStyles}>
      <CardContent sx={{ p: 2.75 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
          Répartition des paiements
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Vue simple du chiffre d’affaires par mode de paiement.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
          <Box
            sx={{
              width: 190,
              height: 190,
              borderRadius: '50%',
              background: `conic-gradient(#27A8AE 0 ${cashPercent}%, #5cc4c7 ${cashPercent}% 100%)`,
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 20,
                borderRadius: '50%',
                bgcolor: 'background.paper',
                display: 'grid',
                placeItems: 'center',
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'rgba(226,232,240,0.9)',
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                  {formatCompact(totalRevenue)} DH
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  total période
                </Typography>
              </Box>
            </Box>
          </Box>

          <Stack spacing={1.5} sx={{ width: '100%' }}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, bgcolor: 'grey.50' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }} />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Espèce
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {formatCurrency(cashRevenue)} DH
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {cashOrders} paiements • {formatPercent((cashRevenue / normalizedTotal) * 100)}
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, bgcolor: 'grey.50' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#5cc4c7' }} />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Carte
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {formatCurrency(cardRevenue)} DH
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {cardOrders} paiements • {formatPercent((cardRevenue / normalizedTotal) * 100)}
              </Typography>
            </Paper>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

interface TopProductsCardProps {
  products: ProductAggregate[];
}

const TopProductsCard: React.FC<TopProductsCardProps> = ({ products }) => (
  <Card sx={cardStyles}>
    <CardContent sx={{ p: 2.75 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Top produits
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Volume réel vendu et nombre de commandes.
          </Typography>
        </Box>
        <Chip label={`Top ${Math.min(products.length, 8)}`} />
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Produit</TableCell>
            <TableCell align="right">Commandes</TableCell>
            <TableCell align="right">Quantité</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.slice(0, 8).map((product, index) => (
            <TableRow key={product.id}>
              <TableCell>
                <Chip
                  label={index + 1}
                  size="small"
                  sx={{ minWidth: 34, fontWeight: 800, bgcolor: 'rgba(39,168,174,0.12)', color: 'primary.dark' }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {product.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {product.category}
                </Typography>
              </TableCell>
              <TableCell align="right">{product.orderCount}</TableCell>
              <TableCell align="right">{product.quantity}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

interface CategoriesCardProps {
  categories: CategoryAggregate[];
}

const CategoriesCard: React.FC<CategoriesCardProps> = ({ categories }) => {
  const maxOrders = Math.max(...categories.map((category) => category.orderCount), 1);

  return (
    <Card sx={cardStyles}>
      <CardContent sx={{ p: 2.75 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
          Catégories
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Répartition des commandes et quantités par catégorie.
        </Typography>

        <Stack spacing={2}>
          {categories.slice(0, 6).map((category) => (
            <Box key={category.name}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.8}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {category.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {category.quantity} articles vendus
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {category.orderCount} cmd
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={(category.orderCount / maxOrders) * 100}
                sx={{
                  height: 10,
                  borderRadius: 999,
                  bgcolor: '#eaf0f6',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    background: 'linear-gradient(90deg, #27A8AE 0%, #72d1d4 100%)',
                  },
                }}
              />
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

interface InsightsCardProps {
  featuredProducts: ProductAggregate[];
  topStore?: StoreAggregate;
  cancelledOrders: number;
  activeOrders: number;
  totalOrders: number;
}

const InsightsCard: React.FC<InsightsCardProps> = ({
  featuredProducts,
  topStore,
  cancelledOrders,
  activeOrders,
  totalOrders,
}) => {
  const leadingFeatured = featuredProducts[0];
  const cancellationRate = totalOrders > 0 ? (cancelledOrders / totalOrders) * 100 : 0;

  return (
    <Card sx={cardStyles}>
      <CardContent sx={{ p: 2.75 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
          Insights rapides
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Lecture direction simple des points importants de la période.
        </Typography>

        <Stack spacing={1.5}>
          <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.8)' }}>
            <Stack direction="row" spacing={1.2} alignItems="center" mb={1}>
              <LocalFireDepartmentRoundedIcon color="primary" />
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                Produit vedette
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {leadingFeatured
                ? `${leadingFeatured.name} mène les produits mis en avant avec ${leadingFeatured.quantity} ventes sur la période.`
                : 'Aucun produit vedette configuré n’apparaît sur la période sélectionnée.'}
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.8)' }}>
            <Stack direction="row" spacing={1.2} alignItems="center" mb={1}>
              <StoreMallDirectoryRoundedIcon color="primary" />
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                Magasin le plus performant
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {topStore
                ? `${topStore.name} est en tête avec ${formatCurrency(topStore.revenue)} DH et ${topStore.orders} commandes.`
                : 'Aucun magasin n’a généré de ventes sur la période choisie.'}
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.8)' }}>
            <Stack direction="row" spacing={1.2} alignItems="center" mb={1}>
              <ReceiptLongRoundedIcon color="primary" />
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                Situation opérationnelle
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {activeOrders > 0
                ? `${activeOrders} commandes sont encore en cours. Taux d’annulation actuel : ${formatPercent(cancellationRate)}.`
                : `Aucune commande active actuellement. Taux d’annulation : ${formatPercent(cancellationRate)}.`}
            </Typography>
          </Paper>
        </Stack>
      </CardContent>
    </Card>
  );
};

const SalesStatistics: React.FC = () => {
  const {
    loading: scopeLoading,
    activeScopeType,
    selectedGroupId,
    selectedStoreId: scopedStoreId,
    selectedGroup,
    selectedStore,
    stores: scopeStores,
  } = useAdminScope();
  const [preset, setPreset] = useState<RangePreset>('today');
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(undefined);
  const [selectedStoreId, setSelectedStoreId] = useState<FilterValue>('all');
  const [selectedPayment, setSelectedPayment] = useState<FilterValue>('all');
  const [selectedDelivery, setSelectedDelivery] = useState<FilterValue>('all');
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [customerTotal, setCustomerTotal] = useState(0);
  const [customerPeriodCount, setCustomerPeriodCount] = useState(0);
  const [existingUsersTotal, setExistingUsersTotal] = useState(0);
  const [salesSummary, setSalesSummary] = useState<SalesScopeDailySummary | null>(null);
  const [otpAuthStats, setOtpAuthStats] = useState<OtpAuthStats>(emptyOtpAuthStats());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadRequestIdRef = React.useRef(0);
  const scopedStoreIds = useMemo(
    () =>
      resolveScopedStoreIds({
        activeScopeType,
        selectedGroupId,
        selectedStoreId: scopedStoreId,
        stores: scopeStores,
      }),
    [activeScopeType, selectedGroupId, scopedStoreId, scopeStores]
  );
  const scopeSummary = useMemo(
    () =>
      describeActiveScope({
        activeScopeType,
        selectedGroupId,
        selectedStoreId: scopedStoreId,
        stores: scopeStores,
      }),
    [activeScopeType, selectedGroupId, scopedStoreId, scopeStores]
  );
  const isScopedMode = scopedStoreIds !== null;
  const sortedScopedStoreIds = useMemo(
    () => (scopedStoreIds === null ? null : [...scopedStoreIds].sort()),
    [scopedStoreIds]
  );
  const isScopeReady = useMemo(() => {
    if (scopeLoading) {
      return false;
    }

    if (activeScopeType === 'global') {
      return true;
    }

    return Boolean(scopedStoreId || selectedGroupId) || scopeStores.length === 0;
  }, [scopeLoading, activeScopeType, scopedStoreId, selectedGroupId, scopeStores.length]);

  const activeDateRange = useMemo(
    () => (preset === 'custom' ? customDateRange : getPresetRange(preset)),
    [preset, customDateRange]
  );

  const salesAggregateScope = useMemo<SalesScopeAggregate>(() => {
    if (selectedStoreId !== 'all') {
      return { scopeType: 'store', scopeId: selectedStoreId };
    }

    if (selectedGroupId) {
      return { scopeType: 'group', scopeId: selectedGroupId };
    }

    if (scopedStoreId) {
      return { scopeType: 'store', scopeId: scopedStoreId };
    }

    return { scopeType: 'global', scopeId: 'global' };
  }, [selectedGroupId, scopedStoreId, selectedStoreId]);

  const canUseSalesAggregateQuery = useMemo(
    () =>
      !!activeDateRange &&
      DateUtils.isFullDayRange(activeDateRange) &&
      selectedPayment === 'all' &&
      selectedDelivery === 'all',
    [activeDateRange, selectedDelivery, selectedPayment]
  );
  const salesCacheKey = useMemo(
    () =>
      JSON.stringify({
        activeScopeType,
        selectedGroupId,
        scopedStoreId,
        scopedStoreIds: sortedScopedStoreIds,
        preset,
        dateStart: activeDateRange?.start.toISOString() || null,
        dateEnd: activeDateRange?.end.toISOString() || null,
        selectedStoreId,
        selectedPayment,
        selectedDelivery,
      }),
    [
      activeScopeType,
      selectedGroupId,
      scopedStoreId,
      sortedScopedStoreIds,
      preset,
      activeDateRange,
      selectedStoreId,
      selectedPayment,
      selectedDelivery,
    ]
  );

  useEffect(() => {
    if (scopedStoreId) {
      if (selectedStoreId !== scopedStoreId) {
        setSelectedStoreId(scopedStoreId || 'all');
      }
      return;
    }

    if (selectedStoreId !== 'all') {
      setSelectedStoreId('all');
    }
  }, [scopedStoreId, selectedGroupId, selectedStoreId]);

  const loadData = useCallback(async (silent = false) => {
    const requestId = ++loadRequestIdRef.current;
    const cachedData = salesStatisticsCache?.key === salesCacheKey ? salesStatisticsCache : null;

    try {
      if (cachedData) {
        setOrders(cachedData.orders);
        setProducts(cachedData.products);
        setStores(cachedData.stores);
        setCustomerTotal(cachedData.customerTotal);
        setCustomerPeriodCount(cachedData.customerPeriodCount);
        setExistingUsersTotal(cachedData.existingUsersTotal);
        setSalesSummary(cachedData.salesSummary);
        setOtpAuthStats(cachedData.otpAuthStats);
        setLoading(false);
        setRefreshing(true);
      } else if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      if (!isScopeReady) {
        return;
      }

      const effectiveStoreId = selectedStoreId !== 'all' ? selectedStoreId : undefined;
      const effectiveScopedStoreIds = effectiveStoreId ? [effectiveStoreId] : scopedStoreIds;

      const filters = buildOrderFilters(
        activeDateRange,
        effectiveStoreId || 'all',
        selectedPayment,
        selectedDelivery
      );

      const scopedCustomerScope = effectiveStoreId
        ? { scopeType: 'store' as const, scopeId: effectiveStoreId }
        : selectedGroupId
          ? { scopeType: 'group' as const, scopeId: selectedGroupId }
          : scopedStoreId
            ? { scopeType: 'store' as const, scopeId: scopedStoreId }
            : null;

      const salesSummaryPromise = canUseSalesAggregateQuery && activeDateRange
        ? salesScopeDailyService
            .getSummary(salesAggregateScope, activeDateRange)
            .catch((aggregateError) => {
              console.warn('sales_scope_daily indisponible, fallback orders:', aggregateError);
              return null;
            })
        : Promise.resolve(null);

      const [ordersDataRaw, productsDataRaw, storesData, totalCustomersResult, periodCustomersResult, otpStatsResult, scopedCustomerTotalResult, existingUsersTotalResult, salesSummaryResult] = await Promise.all([
        orderService.getOrders(filters, {
          enrichUsers: false,
          enrichProducts: false,
        }),
        productService.getProducts(
          effectiveStoreId
            ? { storeId: effectiveStoreId }
            : ({ storeId: null } as any)
        ),
        storeService.getActiveStores(),
        isScopedMode ? Promise.resolve({ count: 0 }) : customerService.countCustomers(),
        isScopedMode
          ? Promise.resolve({ count: 0 })
          : customerService.countCustomers(activeDateRange ? { dateRange: activeDateRange } : undefined),
        loadOtpAuthStats(activeDateRange),
        isScopedMode
          ? (scopedCustomerScope
              ? customerService.countScopedCustomers(scopedCustomerScope)
              : Promise.resolve({ count: 0 }))
          : Promise.resolve({ count: 0 }),
        customerService.countCustomers(),
        salesSummaryPromise,
      ]);

      const filteredOrders = filterOrdersByStoreScope(ordersDataRaw, effectiveScopedStoreIds);
      const filteredStores =
        effectiveScopedStoreIds === null
          ? storesData
          : storesData.filter((store) => effectiveScopedStoreIds.includes(store.id));
      const filteredProducts =
        effectiveScopedStoreIds === null
          ? productsDataRaw
          : productsDataRaw.filter((product) => effectiveScopedStoreIds.includes(product.storeId || ''));

      if (requestId !== loadRequestIdRef.current) {
        return;
      }

      const nextCustomerTotal = !isScopedMode
        ? totalCustomersResult.count
        : (typeof scopedCustomerTotalResult?.count === 'number' ? scopedCustomerTotalResult.count : 0);
      const nextCustomerPeriodCount = !isScopedMode
        ? periodCustomersResult.count
        : new Set(filteredOrders.map((order) => order.userId)).size;

      salesStatisticsCache = {
        key: salesCacheKey,
        orders: filteredOrders,
        products: filteredProducts,
        stores: filteredStores,
        customerTotal: nextCustomerTotal,
        customerPeriodCount: nextCustomerPeriodCount,
        existingUsersTotal: existingUsersTotalResult.count,
        salesSummary: salesSummaryResult,
        otpAuthStats: otpStatsResult,
      };

      setOrders(filteredOrders);
      setProducts(filteredProducts);
      setStores(filteredStores);
      setExistingUsersTotal(existingUsersTotalResult.count);
      setSalesSummary(salesSummaryResult);
      if (!isScopedMode) {
        setCustomerTotal(nextCustomerTotal);
        setCustomerPeriodCount(nextCustomerPeriodCount);
      } else {
        setCustomerTotal(nextCustomerTotal);
        setCustomerPeriodCount(nextCustomerPeriodCount);
      }
      setOtpAuthStats(otpStatsResult);
    } catch (err) {
      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      console.error('Erreur lors du chargement des statistiques de vente:', err);
      setSalesSummary(null);
      setError('Impossible de charger les statistiques de vente pour le moment.');
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [
    activeDateRange,
    canUseSalesAggregateQuery,
    isScopedMode,
    isScopeReady,
    salesAggregateScope,
    salesCacheKey,
    scopedStoreId,
    scopedStoreIds,
    selectedDelivery,
    selectedGroupId,
    selectedPayment,
    selectedStoreId,
  ]);

  useEffect(() => {
    if (preset === 'custom' && !customDateRange) {
      return;
    }
    if (!isScopeReady) {
      return;
    }
    void loadData();
  }, [preset, customDateRange, loadData, isScopeReady]);

  const productMap = useMemo(() => new Map(products.map((product) => [product.id, product])), [products]);

  const fallbackStats = useMemo(
    () => buildDerivedStats(orders, productMap, activeDateRange),
    [orders, productMap, activeDateRange]
  );

  const shouldUseSalesAggregate = useMemo(
    () => canUseSalesAggregateQuery && hasCompleteAggregateCoverage(salesSummary, orders),
    [canUseSalesAggregateQuery, orders, salesSummary]
  );

  const stats = useMemo(() => {
    if (!shouldUseSalesAggregate || !salesSummary) {
      return fallbackStats;
    }

    return {
      ...fallbackStats,
      ...toAggregateDrivenStats(salesSummary),
    };
  }, [fallbackStats, salesSummary, shouldUseSalesAggregate]);

  const deliveryShare = stats.totalOrders > 0 ? (stats.deliveryOrders / stats.totalOrders) * 100 : 0;
  const pickupShare = stats.totalOrders > 0 ? (stats.pickupOrders / stats.totalOrders) * 100 : 0;
  const cardShare = stats.totalOrders > 0 ? (stats.cardOrders / stats.totalOrders) * 100 : 0;
  const cashShare = stats.totalOrders > 0 ? (stats.cashOrders / stats.totalOrders) * 100 : 0;
  const customerKpiTitle = isScopedMode ? 'Clients acheteurs' : 'Base clients réseau';
  const customerKpiFooterLeft = isScopedMode
    ? `${formatInteger(customerPeriodCount)} acheteurs sur période`
    : `+${formatInteger(customerPeriodCount)} créés sur période`;
  const customerKpiFooterRight = isScopedMode
    ? `Base users : ${formatInteger(existingUsersTotal)}`
    : 'Collection users totale';

  return (
    <>
      <Box mb={4}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'flex-start' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, letterSpacing: '-0.04em' }}>
              Ventes &amp; Statistiques
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Une vue claire des commandes, revenus, produits, catégories et paiements avec
              filtres par période et magasin.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadData(true)}
            disabled={loading || refreshing}
          >
            Actualiser
          </Button>
        </Stack>
        <Alert severity="info" sx={{ mt: 2 }}>
          Scope actif: {scopeSummary}
          {selectedGroup ? ` · Groupe ${selectedGroup.name}` : ''}
          {selectedStore ? ` · Magasin ${selectedStore.name}` : ''}
        </Alert>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          position: 'sticky',
          top: 12,
          zIndex: 5,
          borderRadius: 4,
          border: '1px solid rgba(226,232,240,0.9)',
          backdropFilter: 'blur(18px)',
          bgcolor: 'rgba(248,250,252,0.92)',
          boxShadow: '0 18px 42px rgba(15, 23, 42, 0.08)',
        }}
      >
        <Stack direction="row" gap={1} flexWrap="wrap" mb={2}>
          {quickPresetOptions.map((option) => (
            <Chip
              key={option.value}
              label={option.label}
              onClick={() => setPreset(option.value)}
              color={preset === option.value ? 'primary' : 'default'}
              variant={preset === option.value ? 'filled' : 'outlined'}
              sx={{ fontWeight: 700 }}
            />
          ))}
        </Stack>

        {preset === 'custom' ? (
          <Box mb={2}>
            <DateRangePicker
              value={customDateRange}
              onChange={setCustomDateRange}
              label="Période personnalisée"
              showQuickOptions={false}
              includeTime
            />
          </Box>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', lg: 'repeat(4, minmax(0,1fr))' },
            gap: 1.5,
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel>Magasin</InputLabel>
            <Select
              value={selectedStoreId}
              label="Magasin"
              onChange={(event) => setSelectedStoreId(event.target.value)}
              disabled={activeScopeType === 'store' || !!scopedStoreId}
            >
              <MenuItem value="all">
                {activeScopeType === 'group' ? 'Tous les magasins du groupe' : 'Tous les magasins'}
              </MenuItem>
              {stores.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  {store.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Paiement</InputLabel>
            <Select
              value={selectedPayment}
              label="Paiement"
              onChange={(event) => setSelectedPayment(event.target.value)}
            >
              {paymentOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Canal</InputLabel>
            <Select
              value={selectedDelivery}
              label="Canal"
              onChange={(event) => setSelectedDelivery(event.target.value)}
            >
              {deliveryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {refreshing ? <LinearProgress sx={{ mb: 2, borderRadius: 999 }} /> : null}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0,1fr))',
                lg: 'repeat(4, minmax(0,1fr))',
                xl: 'repeat(5, minmax(0,1fr))',
              },
              gap: 2,
              mb: 3,
            }}
          >
            <KpiCard
              icon={<ReceiptLongRoundedIcon />}
              title="Total commandes"
              value={formatCompact(stats.totalOrders)}
              footerLeft={`Livrées : ${stats.deliveredOrders}`}
              footerRight={`Annulées : ${stats.cancelledOrders}`}
            />
            <KpiCard
              icon={<PaymentsRoundedIcon />}
              title="Chiffre d’affaires"
              value={formatCurrencyFull(stats.totalRevenue)}
              footerLeft="Période filtrée"
              footerRight={`Panier moy. ${formatCurrency(stats.averageOrderValue)} DH`}
            />
            <KpiCard
              icon={<ShoppingBagRoundedIcon />}
              title="Panier moyen"
              value={`${formatCurrency(stats.averageOrderValue)} DH`}
              footerLeft={`Commandes : ${stats.totalOrders}`}
              footerRight={`Actives : ${stats.activeOrders}`}
            />
            <KpiCard
              icon={<DeliveryDiningRoundedIcon />}
              title="Livraison"
              value={formatInteger(stats.deliveryOrders)}
              footerLeft={`${formatInteger(stats.deliveryRevenue)} DH`}
              footerRight={`${formatPercent(deliveryShare)} du total commandes`}
            />
            <KpiCard
              icon={<StoreMallDirectoryRoundedIcon />}
              title="Pick-up magasin"
              value={formatInteger(stats.pickupOrders)}
              footerLeft={`${formatInteger(stats.pickupRevenue)} DH`}
              footerRight={`${formatPercent(pickupShare)} du total commandes`}
            />
            <KpiCard
              icon={<CreditCardRoundedIcon />}
              title="Paiement carte"
              value={formatInteger(stats.cardOrders)}
              footerLeft={`${formatInteger(stats.cardRevenue)} DH`}
              footerRight={`${formatPercent(cardShare)} du total paiements`}
            />
            <KpiCard
              icon={<PointOfSaleRoundedIcon />}
              title="Paiement espèce"
              value={formatInteger(stats.cashOrders)}
              footerLeft={`${formatInteger(stats.cashRevenue)} DH`}
              footerRight={`${formatPercent(cashShare)} du total paiements`}
            />
            <KpiCard
              icon={<Groups2RoundedIcon />}
              title={customerKpiTitle}
              value={formatInteger(customerTotal)}
              footerLeft={customerKpiFooterLeft}
              footerRight={customerKpiFooterRight}
            />
            <KpiCard
              icon={<LocalOfferRoundedIcon />}
              title="Codes promo utilisés"
              value={`${formatInteger(stats.promoUses)} utilisations`}
              footerLeft={`${stats.uniquePromoCodes} codes distincts`}
              footerRight={formatCurrencyFull(stats.promoAmountTotal)}
            />
            <DeliveryMarginCard
              totalDeliveryFees={stats.totalDeliveryFees}
              totalDriverCommission={stats.totalDriverCommission}
              netDeliveryRevenue={stats.netDeliveryRevenue}
              deliveryOrders={stats.deliveryOrders}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <OtpAuthCard stats={otpAuthStats} />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', xl: '1.7fr 1fr' },
              gap: 2,
              mb: 3,
            }}
          >
            <RevenueChart timeline={stats.timeline} />
            <PaymentBreakdownCard
              cashRevenue={stats.cashRevenue}
              cardRevenue={stats.cardRevenue}
              cashOrders={stats.cashOrders}
              cardOrders={stats.cardOrders}
              totalRevenue={stats.totalRevenue}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', xl: '1.15fr 1.05fr 0.9fr' },
              gap: 2,
            }}
          >
            <TopProductsCard products={stats.topProducts} />
            <CategoriesCard categories={stats.topCategories} />
            <InsightsCard
              featuredProducts={stats.featuredProducts}
              topStore={stats.topStore}
              cancelledOrders={stats.cancelledOrders}
              activeOrders={stats.activeOrders}
              totalOrders={stats.totalOrders}
            />
          </Box>
        </>
      )}
    </>
  );
};

export default SalesStatistics;
